import { useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { compressToDataUrl } from "@/lib/image";
import { useLiveRefresh } from "@/hooks/useLiveRefresh";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Fuel, Plus, Loader2, CreditCard, AlertTriangle, Gauge, Clock, Upload, FileCheck2, Receipt } from "lucide-react";
import { toast } from "sonner";

const FUEL_TYPES = ["Benzina", "Diesel", "GPL", "Metano", "Elettrico", "AdBlue"];
const fmtEur = (n) => "€ " + new Intl.NumberFormat("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }) : "-");

const STATUS_STYLE = {
  in_attesa: { label: "In attesa", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  approvata: { label: "Approvata", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  rifiutata: { label: "Rifiutata", cls: "bg-rose-100 text-rose-700 border-rose-200" },
};

function StatusPill({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.in_attesa;
  return <span data-testid={`fuel-status-${status}`} className={`rounded-full border px-2.5 py-0.5 text-xs font-bold ${s.cls}`}>{s.label}</span>;
}

const emptyForm = { vehicle_id: "", usage_type: "km", usage_value: "", fuel_type: "Diesel", amount: "", liters: "", notes: "" };
const toNum = (s) => { const n = parseFloat(String(s).replace(",", ".")); return isNaN(n) ? undefined : n; };

export default function MobileCarburante() {
  const [items, setItems] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [cards, setCards] = useState([]);
  const [cardId, setCardId] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/fuel-requests").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
    api.get("/me/fuel-cards").then((r) => { setCards(r.data); if (r.data.length) setCardId(r.data[0].id); }).catch(() => {});
  }, []);
  useLiveRefresh(load);

  const hasCard = cards.length > 0;

  const submit = async () => {
    if (!form.vehicle_id) return toast.error("Seleziona un mezzo");
    if (!hasCard) return toast.error("Nessuna carta carburante assegnata. Contatta l'amministratore.");
    if (!form.usage_value || !form.amount) return toast.error("Compila valore e importo");
    setSaving(true);
    try {
      await api.post("/fuel-requests", {
        ...form, fuel_card_id: cardId,
        usage_value: toNum(form.usage_value), amount: toNum(form.amount),
        liters: form.liters ? toNum(form.liters) : undefined,
      });
      toast.success("Richiesta inviata, in attesa di autorizzazione");
      setOpen(false); setForm(emptyForm); load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-navy">Carburante</h1>
          <p className="text-sm text-muted-foreground">Le tue richieste di rifornimento</p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="mobile-new-fuel-btn" className="rounded-full">
          <Plus size={18} /> Nuova
        </Button>
      </div>

      {!hasCard && (
        <div className="flex items-center gap-3 rounded-lg border border-rose-200 bg-rose-50 p-3" data-testid="mobile-no-card">
          <AlertTriangle className="h-5 w-5 shrink-0 text-rose-600" />
          <p className="text-sm text-rose-700">Nessuna carta carburante assegnata. Contatta l'amministratore per poter inviare richieste.</p>
        </div>
      )}

      {items.length === 0 ? (
        <Card className="flex flex-col items-center gap-2 py-14 text-center">
          <Fuel className="h-9 w-9 text-muted-foreground/40" />
          <p className="font-semibold text-foreground">Nessuna richiesta</p>
          <p className="text-sm text-muted-foreground">Tocca "Nuova" per creare la prima.</p>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="mobile-fuel-list">
          {items.map((r) => (
            <Card key={r.id} data-testid={`fuel-card-${r.id}`} className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-bold text-navy">{r.vehicle_name} {r.vehicle_plate && <span className="text-xs font-normal text-muted-foreground">{r.vehicle_plate}</span>}</p>
                  <p className="text-xs text-muted-foreground">{fmtDate(r.created_at)}</p>
                </div>
                <StatusPill status={r.status} />
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-muted-foreground">
                <span className="flex items-center gap-1.5">{r.usage_type === "km" ? <Gauge size={15} /> : <Clock size={15} />}{r.usage_value} {r.usage_type === "km" ? "km" : "ore"}</span>
                <span className="flex items-center gap-1.5"><Fuel size={15} />{r.fuel_type}</span>
                <span className="ml-auto text-lg font-extrabold text-primary">{fmtEur(r.amount)}</span>
              </div>
              {r.notes && <p className="mt-2 text-sm text-muted-foreground">{r.notes}</p>}
              {r.status === "rifiutata" && r.review_note && (
                <p className="mt-2 rounded-md bg-secondary/60 p-2 text-sm"><b>Nota:</b> {r.review_note}</p>
              )}
              {r.status === "approvata" && (
                <div className="mt-3">
                  {r.receipt_url ? (
                    <div className="flex flex-wrap items-center gap-3 text-sm">
                      <a href={r.receipt_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 font-semibold text-primary" data-testid={`fuel-view-receipt-${r.id}`}><Receipt size={15} /> Scontrino</a>
                      {r.actual_amount != null && <span className="text-muted-foreground">Effettivo: <b className="text-foreground">{fmtEur(r.actual_amount)}</b></span>}
                      {r.liters != null && <span className="text-muted-foreground">Litri: <b className="text-foreground">{String(r.liters).replace(".", ",")}</b></span>}
                    </div>
                  ) : (
                    <ReceiptUploader request={r} onDone={load} />
                  )}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="mobile-fuel-dialog" className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nuova richiesta carburante</DialogTitle>
            <DialogDescription>Compila i dati del rifornimento da autorizzare.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {cards.length > 1 && (
              <div>
                <Label className="flex items-center gap-1.5"><CreditCard size={14} /> Carta carburante</Label>
                <Select value={cardId} onValueChange={setCardId}>
                  <SelectTrigger className="mt-1.5" data-testid="mobile-card-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{cards.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Mezzo</Label>
              <Select value={form.vehicle_id} onValueChange={(v) => setForm({ ...form, vehicle_id: v })}>
                <SelectTrigger className="mt-1.5" data-testid="mobile-vehicle-select"><SelectValue placeholder="Seleziona un mezzo" /></SelectTrigger>
                <SelectContent>
                  {vehicles.length === 0 && <div className="px-2 py-3 text-sm text-muted-foreground">Nessun mezzo disponibile</div>}
                  {vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.name} {v.plate ? `(${v.plate})` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tipo di indicazione</Label>
              <RadioGroup value={form.usage_type} onValueChange={(v) => setForm({ ...form, usage_type: v })} className="mt-2 flex gap-6">
                <div className="flex items-center gap-2"><RadioGroupItem value="km" id="m-km" data-testid="mobile-usage-km" /><Label htmlFor="m-km" className="font-normal">Km percorsi</Label></div>
                <div className="flex items-center gap-2"><RadioGroupItem value="ore" id="m-ore" data-testid="mobile-usage-ore" /><Label htmlFor="m-ore" className="font-normal">Ore di utilizzo</Label></div>
              </RadioGroup>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{form.usage_type === "km" ? "Km percorsi" : "Ore"}</Label>
                <Input type="number" step="0.1" min="0" data-testid="mobile-usage-value" value={form.usage_value} onChange={(e) => setForm({ ...form, usage_value: e.target.value })} className="mt-1.5" placeholder={form.usage_type === "km" ? "es. 320" : "es. 8"} />
              </div>
              <div>
                <Label>Importo (€)</Label>
                <Input type="text" inputMode="decimal" data-testid="mobile-amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1.5" placeholder="es. 75,00" />
              </div>
            </div>
            <div>
              <Label>Litri (facoltativo, dallo scontrino)</Label>
              <Input type="text" inputMode="decimal" data-testid="mobile-liters" value={form.liters} onChange={(e) => setForm({ ...form, liters: e.target.value })} className="mt-1.5" placeholder="es. 51,26" />
            </div>
            <div>
              <Label>Tipo di carburante</Label>
              <Select value={form.fuel_type} onValueChange={(v) => setForm({ ...form, fuel_type: v })}>
                <SelectTrigger className="mt-1.5" data-testid="mobile-fuel-type"><SelectValue /></SelectTrigger>
                <SelectContent>{FUEL_TYPES.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Note (facoltative)</Label>
              <Textarea data-testid="mobile-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={submit} disabled={saving || !hasCard} data-testid="mobile-submit-fuel">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Invia richiesta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ReceiptUploader({ request, onDone }) {
  const inputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [dataUrl, setDataUrl] = useState("");
  const [amount, setAmount] = useState(request.amount != null ? String(request.amount).replace(".", ",") : "");
  const [liters, setLiters] = useState(request.liters != null ? String(request.liters).replace(".", ",") : "");
  const [busy, setBusy] = useState(false);

  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try { setDataUrl(await compressToDataUrl(f)); } catch { toast.error("Immagine non valida"); }
  };
  const submit = async () => {
    if (!dataUrl) return toast.error("Scegli la foto dello scontrino");
    const amt = parseFloat(String(amount).replace(",", "."));
    if (!amt || amt <= 0) return toast.error("Inserisci l'importo effettivo");
    const lit = parseFloat(String(liters).replace(",", "."));
    if (!lit || lit <= 0) return toast.error("Inserisci i litri prelevati");
    setBusy(true);
    try {
      await api.put(`/fuel-requests/${request.id}/receipt`, { receipt_data: dataUrl, actual_amount: amt, liters: lit });
      toast.success("Scontrino caricato"); setOpen(false); onDone?.();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <>
      <div className="flex items-center gap-3">
        <Button size="sm" onClick={() => setOpen(true)} data-testid={`fuel-upload-receipt-${request.id}`}><Upload size={15} /> Carica scontrino</Button>
        <span className="text-xs text-muted-foreground">Dopo il rifornimento carica la foto</span>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="mobile-receipt-dialog">
          <DialogHeader><DialogTitle>Carica scontrino</DialogTitle><DialogDescription>Allega la foto e indica l'importo effettivo e i litri dello scontrino.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="rounded-lg border bg-secondary/50 p-3 text-sm">Importo autorizzato: <b>{fmtEur(request.amount)}</b></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Importo effettivo (€)</Label>
                <Input type="text" inputMode="decimal" data-testid="mobile-receipt-amount" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1.5" placeholder="es. 48,30" />
              </div>
              <div>
                <Label>Litri prelevati</Label>
                <Input type="text" inputMode="decimal" data-testid="mobile-receipt-liters" value={liters} onChange={(e) => setLiters(e.target.value)} className="mt-1.5" placeholder="es. 51,26" />
              </div>
            </div>
            <input ref={inputRef} type="file" accept="image/*" capture="environment" className="hidden" data-testid="mobile-receipt-file" onChange={pick} />
            <Button variant="outline" className="w-full" onClick={() => inputRef.current?.click()} data-testid="mobile-receipt-pick">
              {dataUrl ? <FileCheck2 className="mr-2 h-4 w-4 text-emerald-600" /> : <Upload className="mr-2 h-4 w-4" />}{dataUrl ? "Foto pronta" : "Scegli / scatta foto"}
            </Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={submit} disabled={busy} data-testid="mobile-receipt-confirm">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Conferma</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
